import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import classNames from 'classnames';
import { Row, Col } from 'reactstrap';
import { NSButton, NSLabel } from 'bricks';
import NSInputNumberStepper from 'bricks/NSInputNumberStepper/NSInputNumberStepper';
import './OperatingInformation.scss';
import { IOperatingAndPermanentDebtInfo, IPatchOperatingInfoData } from 'views/ProFormaTable/types';
import RenderIf from 'components/RenderIf/RenderIf';
import { ProFormaModelIds, IncomeCalculationType } from 'constants/proForma';
import { NSButtonBase } from 'bricks/NSButton/NSButton';
import { ProjectActualsDiscrepanciesEnum } from 'views/ProFormaTable/constants';
import MonthlyAnalysisTable, { IMonthlyAnalysisPeriod } from './MonthlyAnalysisTable/MonthlyAnalysisTable';
import { useProFormaSocketContext } from '../../socketContext/ProFormaSocketProvider';
import { getMonthlyAnalysisPeriods } from './helpers';
import MetricOutputCard from '../../../MetricOutputCard/MetricOutputCard';

export interface IOperatingInformationProps {
    signUpMode?: boolean;
}

const OperatingInformation = ({ signUpMode = false }: IOperatingInformationProps) => {
    const {
        proForma, operatingInfo, operatingPeriodCashFlows, handleUpdateProFormaOperations,
        cashFlowPeriods, shouldUseProjectActuals, projectActuals,
    } = useProFormaSocketContext();
    const isIncomeCalculationTypeEstimate = operatingInfo?.incomeCalculationType === IncomeCalculationType.ESTIMATE;

    const [operatingInfoData, setOperatingInfoData] = useState<Partial<IOperatingAndPermanentDebtInfo>>({});

    const labelClassName = 'pl-0 col-4 font-weight-normal text-dark align-self-center';
    const labelClassNameNoWrap = 'pl-0 col-4 my-auto font-weight-normal text-dark text-nowrap';

    const actualsDiscrepancies = projectActuals?.projectActuals?.discrepancies || [];

    const updateOperationsKeys = (keyValues: Partial<IPatchOperatingInfoData>) => {
        if (operatingInfo?.id) {
            handleUpdateProFormaOperations(operatingInfo.id, keyValues);
        }
    };

    const monthlyAnalysisPeriods: IMonthlyAnalysisPeriod[] = getMonthlyAnalysisPeriods(
        proForma, cashFlowPeriods, operatingInfo, operatingPeriodCashFlows,
    );

    useEffect(() => {
        if (operatingInfo) {
            setOperatingInfoData({
                occupancyUponStabilizationPercentage: operatingInfo.occupancyUponStabilizationPercentage,
                rentalGrowthPercentage: operatingInfo.rentalGrowthPercentage,
                percentPreLeased: operatingInfo.percentPreLeased,
                leaseUpPace: operatingInfo.leaseUpPace,
                lengthOfLease: operatingInfo.lengthOfLease,
                initialFreeRent: operatingInfo.initialFreeRent,
                stabilizedFreeRent: operatingInfo.stabilizedFreeRent,
            });
        }
    }, [operatingInfo, operatingPeriodCashFlows]);

    return (
        <Row className="mx-n1 px-3">
            <Col className="px-1 my-3" sm={12} md={6}>
                <div className="d-flex align-items-center">
                    <NSLabel className={`my-auto ${labelClassName}`}>Occupancy upon stabilization</NSLabel>
                    <NSInputNumberStepper
                        percentage
                        name="occupancyUponStabilizationPercentage"
                        value={operatingInfoData.occupancyUponStabilizationPercentage || null}
                        onChange={(value: number) => updateOperationsKeys({
                            occupancyUponStabilizationPercentage: value,
                        })}
                        min={0}
                        step={1}
                        max={100}
                        inputGroupClassName="OperatingInformation__input-width"
                    />
                </div>
                <RenderIf isTrue={proForma.proFormaModelId !== ProFormaModelIds.APARTMENT}>
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className={`my-auto ${labelClassName}`}>Income calculation</NSLabel>
                        <div className="my-auto font-weight-normal text-dark text-nowrap">
                            {
                                signUpMode ? (
                                    <>
                                        <NSButtonBase
                                            color={isIncomeCalculationTypeEstimate ? 'primary' : 'secondary'}
                                            outline={false}
                                            callback={() => updateOperationsKeys({ incomeCalculationType: IncomeCalculationType.ESTIMATE })}
                                            text="Estimate"
                                            className={classNames('px-3', { 'border border-primary bg-primary-lighten': false })}
                                        />
                                        <NSButtonBase
                                            color={isIncomeCalculationTypeEstimate ? 'secondary' : 'primary'}
                                            outline={false}
                                            callback={() => updateOperationsKeys({ incomeCalculationType: IncomeCalculationType.TENANTS })}
                                            text="Input actual tenants"
                                            className={classNames('px-3', {
                                                'border border-primary bg-primary-lighten': false,
                                            })}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <NSButton
                                            color={isIncomeCalculationTypeEstimate ? 'primary' : 'secondary'}
                                            outline={false}
                                            callback={() => updateOperationsKeys({ incomeCalculationType: IncomeCalculationType.ESTIMATE })}
                                            text="Estimate"
                                            className={classNames('px-3', { 'border border-primary bg-primary-lighten': false })}
                                        />
                                        <NSButton
                                            color={!isIncomeCalculationTypeEstimate ? 'primary' : 'secondary'}
                                            outline={false}
                                            callback={() => updateOperationsKeys({ incomeCalculationType: IncomeCalculationType.TENANTS })}
                                            text="Input actual tenants"
                                            className={classNames('px-3', {
                                                'border border-primary bg-primary-lighten': false,
                                            })}
                                        />
                                    </>
                                )
                            }
                        </div>
                    </div>
                </RenderIf>
                <RenderIf isTrue={isIncomeCalculationTypeEstimate || proForma.proFormaModelId === ProFormaModelIds.APARTMENT}>
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className={labelClassNameNoWrap}>Rent growth (annual)</NSLabel>
                        <NSInputNumberStepper
                            percentage
                            name="rentalGrowthPercentage"
                            value={operatingInfoData.rentalGrowthPercentage || null}
                            onChange={(value: number) => updateOperationsKeys({ rentalGrowthPercentage: value })}
                            min={0}
                            step={0.1}
                            max={100}
                            inputGroupClassName="OperatingInformation__input-width"
                        />
                    </div>

                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className={labelClassNameNoWrap}>% pre-leased</NSLabel>
                        <NSInputNumberStepper
                            percentage
                            name="percentPreLeased"
                            value={operatingInfoData.percentPreLeased || null}
                            onChange={(value: number) => updateOperationsKeys({ percentPreLeased: value })}
                            min={0}
                            step={1}
                            max={100}
                            inputGroupClassName="OperatingInformation__input-width"
                        />
                    </div>

                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className={labelClassName}>
                            Lease-up pace
                            {proForma.proFormaModelId === ProFormaModelIds.OFFICE
                                || proForma.proFormaModelId === ProFormaModelIds.INDUSTRIAL
                                || proForma.proFormaModelId === ProFormaModelIds.RETAIL ? ' (suites/month)' : ' (units/month)'}
                        </NSLabel>
                        <NSInputNumberStepper
                            name="leaseUpPace"
                            value={operatingInfoData.leaseUpPace || null}
                            onChange={(value: number) => updateOperationsKeys({ leaseUpPace: value })}
                            min={0}
                            step={1}
                            inputGroupClassName="OperatingInformation__input-width"
                        />
                    </div>

                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className={labelClassNameNoWrap}>Length of lease</NSLabel>
                        <NSInputNumberStepper
                            name="lengthOfLease"
                            value={operatingInfoData.lengthOfLease || null}
                            onChange={(value: number) => updateOperationsKeys({ lengthOfLease: value })}
                            min={1}
                            step={1}
                            customSuffix={(operatingInfo.lengthOfLease || 0) > 1 ? 'months' : 'month'}
                            inputGroupClassName="OperatingInformation__input-width"
                        />
                    </div>
                </RenderIf>
                <RenderIf isTrue={proForma.proFormaModelId === ProFormaModelIds.APARTMENT}>
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className={labelClassNameNoWrap}>Initial free rent</NSLabel>
                        <NSInputNumberStepper
                            name="initialFreeRent"
                            value={operatingInfoData.initialFreeRent || null}
                            onChange={(value: number) => updateOperationsKeys({ initialFreeRent: value })}
                            min={0}
                            step={1}
                            customSuffix={(operatingInfo.initialFreeRent || 0) > 1 ? 'months' : 'month'}
                            inputGroupClassName="OperatingInformation__input-width"
                        />
                    </div>

                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className={labelClassName}>Stabilized free rent</NSLabel>
                        <NSInputNumberStepper
                            name="stabilizedFreeRent"
                            value={operatingInfoData.stabilizedFreeRent || null}
                            onChange={(value: number) => updateOperationsKeys({ stabilizedFreeRent: value })}
                            min={0}
                            step={1}
                            customSuffix={(operatingInfoData.stabilizedFreeRent || 0) > 1 ? 'months' : 'month'}
                            inputGroupClassName="OperatingInformation__input-width"
                        />
                    </div>
                </RenderIf>
            </Col>
            <Col className="px-1 my-3" sm={12} md={6}>
                <div>
                    <MetricOutputCard
                        label="Operation start"
                        value={operatingInfo?.operationStartDate ? format(parseISO(operatingInfo?.operationStartDate), 'MMM yyyy') : ''}
                        showInProgress={!operatingInfo.operationStartDate}
                        showProjectActualsIcon={shouldUseProjectActuals
                            && actualsDiscrepancies?.includes(ProjectActualsDiscrepanciesEnum.OPERATION_START_DATE)}
                        projectActualsIconName={ProjectActualsDiscrepanciesEnum.OPERATION_START_DATE}
                    />
                </div>
                <div className="pt-2">
                    <MetricOutputCard
                        label="1st stabilized month"
                        value={operatingInfo.loanFundingDate
                            ? format(parseISO(operatingInfo.loanFundingDate), 'MMM yyyy') : ''}
                        showInProgress={!operatingInfo.loanFundingDate}
                    />
                </div>
                <MonthlyAnalysisTable
                    monthlyAnalysisPeriods={monthlyAnalysisPeriods}
                    operationBeginMonth={operatingInfo.operationBeginMonth}
                />
            </Col>
        </Row>
    );
};

export default OperatingInformation;
